import {Alert, Paper, Stack, Table, Text, TextInput, Title} from '@mantine/core'
import {useState} from 'react'

import {useEditRights} from './edit-rights-context'
import type {JobDetail, JobDetailAssignment} from './types'
import {useSetThirdPartyUnitNumber} from './use-rerented-equipment'

type RerentedEquipmentSectionProps = {
    job: JobDetail
    onJobUpdated: (job: JobDetail) => void
}

function getAssignmentItemName(assignment: JobDetailAssignment): string {
    const name = assignment.rentalAssetName ?? 'Re-rented item'
    const suffix = assignment.unitNumber ? `, unit ${assignment.unitNumber}` : ''
    return suffix && name.endsWith(suffix) ? name.slice(0, -suffix.length) : name
}

function UnitNumberInput({
    job,
    assignment,
    onJobUpdated,
    onError,
}: {
    job: JobDetail
    assignment: JobDetailAssignment
    onJobUpdated: (job: JobDetail) => void
    onError: (message: string | null) => void
}) {
    const saved = assignment.unitNumber ?? ''
    const [value, setValue] = useState(saved)
    const setUnitNumber = useSetThirdPartyUnitNumber(job.id)

    async function save() {
        const next = value.trim()
        if (next === saved || assignment.rentalAssetId == null) {
            return
        }

        onError(null)
        try {
            onJobUpdated(
                await setUnitNumber.mutateAsync({
                    rentalAssetId: assignment.rentalAssetId,
                    unitNumber: next || null,
                }),
            )
        } catch (error) {
            onError(error instanceof Error ? error.message : 'Failed to save unit number')
        }
    }

    return (
        <TextInput
            aria-label={`Unit number for ${getAssignmentItemName(assignment)}`}
            placeholder="Add unit number"
            size="xs"
            value={value}
            disabled={setUnitNumber.isPending}
            onChange={(event) => setValue(event.currentTarget.value)}
            onBlur={() => void save()}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.currentTarget.blur()
                }
            }}
        />
    )
}

/** Unit number table for the job's third-party assignments. */
export function RerentedEquipmentSection({job, onJobUpdated}: RerentedEquipmentSectionProps) {
    const {canEdit, canDispatch} = useEditRights()
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const rerents = job.assignments.filter((assignment) => assignment.isThirdParty)
    if (rerents.length === 0) {
        return null
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
                <Title order={4}>Re-rented equipment</Title>
                {rerents.some((assignment) => assignment.hasUnitNumbers !== false) ? (
                    <Text size="sm" c="dimmed">
                        Add vendor unit numbers after delivery.
                    </Text>
                ) : null}
                {errorMessage ? (
                    <Alert color="red" title="Failed to save">
                        {errorMessage}
                    </Alert>
                ) : null}
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Item</Table.Th>
                            <Table.Th>Unit number</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rerents.map((assignment) => (
                            <Table.Tr key={assignment.id}>
                                <Table.Td>
                                    <Text size="sm">{getAssignmentItemName(assignment)}</Text>
                                </Table.Td>
                                <Table.Td>
                                    {assignment.hasUnitNumbers === false ? (
                                        <Text size="sm" c="dimmed">
                                            This type does not use unit numbers
                                        </Text>
                                    ) : !canEdit || !canDispatch ? (
                                        <Text
                                            size="sm"
                                            c={assignment.unitNumber ? undefined : 'dimmed'}
                                        >
                                            {assignment.unitNumber ?? 'Not entered'}
                                        </Text>
                                    ) : (
                                        <UnitNumberInput
                                            key={assignment.unitNumber ?? ''}
                                            job={job}
                                            assignment={assignment}
                                            onJobUpdated={onJobUpdated}
                                            onError={setErrorMessage}
                                        />
                                    )}
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Stack>
        </Paper>
    )
}
